import React, { useState } from 'react';
import ReactModal from 'react-modal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faUserCircle, faArrowRight, faUser, faLock, faKey } from '@fortawesome/free-solid-svg-icons';
import Swal from 'sweetalert2';
import { API_URL } from '../../config';
import { useAuth } from './AuthContext';
import { useNavigate } from 'react-router-dom';
import Loading from './Loading';

const overlayStyle = {
    position: 'fixed', inset: 0, backgroundColor: 'rgba(0, 0, 0, 0.5)', display: 'flex',
    alignItems: 'center', justifyContent: 'center', zIndex: 10000, backdropFilter: 'blur(4px)',
};

const contentStyle = {
    position: 'relative', inset: 'auto', border: 'none', background: 'white', borderRadius: '1rem',
    padding: '0', maxWidth: '420px', width: '90%', maxHeight: '90vh', overflow: 'auto',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
};

const LoginPopup = ({ isOpen, onRequestClose }) => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [resetEmail, setResetEmail] = useState('');
    const [mode, setMode] = useState('login');
    const { login } = useAuth();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);

    const handleLogin = async (e) => {
        e.preventDefault();
        if (!email || !password) { Swal.fire({ icon: 'error', title: 'Oops...', html: 'Please fill in both email and password fields.' }); return; }
        if (!isValidEmail(email)) { Swal.fire({ icon: 'error', title: 'Oops...', text: 'Please enter a valid email address.' }); return; }
        try {
            const response = await fetch(API_URL + '/login', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email, password }) });
            const data = await response.json();
            if (response.ok) { login(data); onRequestClose(); navigate('/account'); }
            else { Swal.fire({ icon: 'error', title: 'Oops...', text: data.message || 'Login failed' }); }
        } catch (error) { Swal.fire({ icon: 'error', title: 'Oops...', text: 'An error occurred. Please try again later.' }); }
    };

    const handleResetPassword = async (e) => {
        e.preventDefault();
        if (!isValidEmail(resetEmail)) { Swal.fire({ icon: 'error', title: 'Oops...', text: 'Please enter a valid email address.' }); return; }
        setLoading(true);
        try {
            const response = await fetch(API_URL + '/password/email', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email: resetEmail }) });
            const data = await response.json();
            if (response.ok) { Swal.fire({ icon: 'success', title: 'Reset Password Email Sent', text: data.message || 'Check your email for reset instructions.' }); setMode('login'); }
            else { Swal.fire({ icon: 'error', title: 'Oops...', text: data.message || 'Failed to send reset email' }); }
            setLoading(false);
        } catch (error) { Swal.fire({ icon: 'error', title: 'Oops...', text: 'An error occurred. Please try again later.' }); setLoading(false); }
    };

    const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

    const inputClasses = "tw-w-full tw-rounded-lg tw-border tw-border-gray-400 tw-bg-gray-50 tw-pl-10 tw-pr-4 tw-py-3 tw-text-sm tw-text-gray-800 tw-placeholder-gray-400 tw-outline-none tw-transition-all focus:tw-border-primary-500 focus:tw-ring-2 focus:tw-ring-primary-100 focus:tw-bg-white";

    return (
        <ReactModal isOpen={isOpen} onRequestClose={onRequestClose} style={{ overlay: overlayStyle, content: contentStyle }} ariaHideApp={false}>
            <div className="tw-p-8" style={{ fontFamily: 'Inter, system-ui, -apple-system, sans-serif' }}>
                {loading && <Loading text="Sending password reset link..." />}

                {/* Close button */}
                <button onClick={onRequestClose} className="tw-absolute tw-top-4 tw-right-4 tw-p-2 tw-rounded-lg hover:tw-bg-gray-100 tw-transition-colors tw-border-0 tw-bg-transparent tw-cursor-pointer tw-text-gray-400 hover:tw-text-gray-600">
                    <FontAwesomeIcon icon={faTimes} />
                </button>

                {/* Icon & Title */}
                <div className="tw-text-center tw-mb-6">
                    <div className="tw-text-gray-200 tw-text-6xl tw-mb-3">
                        <FontAwesomeIcon icon={mode === 'login' ? faUserCircle : faKey} />
                    </div>
                    <h2 className="tw-text-2xl tw-text-gray-900 tw-m-0">
                        {mode === 'login' ? 'Sign In' : 'Reset Password'}
                    </h2>
                </div>

                {mode === 'login' ? (
                    <form onSubmit={handleLogin} className="tw-space-y-4">
                        <div className="tw-relative">
                            <FontAwesomeIcon icon={faUser} className="tw-absolute tw-left-3.5 tw-top-1/2 -tw-translate-y-1/2 tw-text-gray-400 tw-text-sm" />
                            <input id="loginEmail" type="text" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputClasses} />
                        </div>
                        <div className="tw-relative">
                            <FontAwesomeIcon icon={faLock} className="tw-absolute tw-left-3.5 tw-top-1/2 -tw-translate-y-1/2 tw-text-gray-400 tw-text-sm" />
                            <input type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} className={inputClasses} />
                        </div>
                        <div className="tw-flex tw-items-center tw-justify-between tw-pt-2">
                            <button type="submit" className="tw-bg-accent-500 hover:tw-bg-accent-600 tw-text-white tw-px-6 tw-py-2.5 tw-rounded-lg tw-text-sm tw-font-semibold tw-border-0 tw-cursor-pointer tw-transition-all tw-shadow-sm hover:tw-shadow-md tw-flex tw-items-center tw-gap-2">
                                Login <FontAwesomeIcon icon={faArrowRight} />
                            </button>
                            <button type="button" onClick={() => setMode('resetPassword')} className="tw-bg-transparent tw-border-0 tw-text-sm tw-text-gray-500 hover:tw-text-primary-600 tw-cursor-pointer tw-transition-colors">
                                Forgot password?
                            </button>
                        </div>
                    </form>
                ) : (
                    <form onSubmit={handleResetPassword} className="tw-space-y-4">
                        <div className="tw-relative">
                            <FontAwesomeIcon icon={faUser} className="tw-absolute tw-left-3.5 tw-top-1/2 -tw-translate-y-1/2 tw-text-gray-400 tw-text-sm" />
                            <input id="resetEmail" type="text" placeholder="Email" value={resetEmail} onChange={(e) => setResetEmail(e.target.value)} className={inputClasses} />
                        </div>
                        <div className="tw-flex tw-items-center tw-justify-between tw-pt-2">
                            <button type="submit" className="tw-bg-accent-500 hover:tw-bg-accent-600 tw-text-white tw-px-6 tw-py-2.5 tw-rounded-lg tw-text-sm tw-font-semibold tw-border-0 tw-cursor-pointer tw-transition-all tw-shadow-sm hover:tw-shadow-md tw-flex tw-items-center tw-gap-2">
                                Reset Password <FontAwesomeIcon icon={faArrowRight} />
                            </button>
                            <button type="button" onClick={() => setMode('login')} className="tw-bg-transparent tw-border-0 tw-text-sm tw-text-gray-500 hover:tw-text-primary-600 tw-cursor-pointer tw-transition-colors">
                                Back to Login
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </ReactModal>
    );
};

export default LoginPopup;
